import { useState } from 'react';

import { useLingui } from '@lingui/react/macro';
import classNames from 'classnames';
import { isToday } from 'date-fns';
import { useAtomValue } from 'jotai';

import type {
  RecommendationFilter,
  SelectedCountsFilter,
} from '../../../@types/appointments';
import { usePreferences } from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectDateAtom, selectedCountsFilterAtom } from '../atoms';
import {
  getIsEnabledByProcedureName,
  useGetRecommendationName,
} from '../patientStrip/patientStripOdontogram/utils';

import AiIndicator from './AiIndicator';
import { FilterButton } from './FilterButton';
import { ViewAllButton } from './ViewAllButton';

export const RecommendationCount = ({
  count,
  selected,
}: {
  count: number;
  selected: boolean;
}) => (
  <p
    className={classNames('vai-text-lg vai-font-inter vai-m-0', {
      'vai-text-sapphire-700 vai-font-medium': selected,
      'vai-text-[#4A495A] vai-font-normal': !selected,
    })}
  >
    {count}
  </p>
);

interface Props {
  filterCounts: Record<RecommendationFilter, number>;
  loading: boolean;
  onChangeFilter: (filter?: SelectedCountsFilter) => void;
}

export const RecommendationFilters = ({
  filterCounts,
  loading,
  onChangeFilter,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const selectedCountsFilter = useAtomValue(selectedCountsFilterAtom);
  const selectedDate = useAtomValue(selectDateAtom);

  const { data: prefs } = usePreferences();
  const { trackEvent } = useTrackEvent();
  const { getRecommendationName } = useGetRecommendationName();
  const { t } = useLingui();

  const handleChangeExpanded = (newIsExpanded: boolean) => {
    setIsExpanded(newIsExpanded);
    trackEvent(mixpanelEvents.userViewAllClick, {
      expanded: newIsExpanded ? 'all' : 'less',
      context: 'RECOMMENDATION_FILTERS',
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  };

  const isEnabledCrown = getIsEnabledByProcedureName('crown', prefs);
  const isEnabledEnamelRegen = getIsEnabledByProcedureName(
    'enamelRegen',
    prefs,
  );
  const isEnabledPerio = getIsEnabledByProcedureName('perio', prefs);
  const isEnabledRootCanal = getIsEnabledByProcedureName('rootCanal', prefs);
  const isEnabledPulpotomy = getIsEnabledByProcedureName('pulpotomy', prefs);
  const isEnabledFilling = getIsEnabledByProcedureName('filling', prefs);
  const isEnabledExtraction = getIsEnabledByProcedureName('extraction', prefs);
  const isEnabledAligner = getIsEnabledByProcedureName('aligner', prefs);
  const isEnabledImplant = getIsEnabledByProcedureName('implant', prefs);

  const isEnabledRootCanalFilter = isEnabledRootCanal || isEnabledPulpotomy;
  const isEnabledOpportunities =
    isEnabledCrown ||
    isEnabledEnamelRegen ||
    isEnabledPerio ||
    isEnabledRootCanalFilter ||
    isEnabledFilling ||
    isEnabledExtraction ||
    isEnabledAligner ||
    isEnabledImplant;

  const canExpand =
    isEnabledRootCanalFilter ||
    isEnabledFilling ||
    isEnabledExtraction ||
    isEnabledAligner ||
    isEnabledImplant;

  return (
    <>
      <AiIndicator text="AI Opportunities" />
      {isEnabledOpportunities && (
        <FilterButton
          count={
            <RecommendationCount
              count={filterCounts.ALL_OPPORTUNITIES}
              selected={selectedCountsFilter === 'ALL_OPPORTUNITIES'}
            />
          }
          dataTestId="all-opportunities-filter"
          loading={loading}
          label={t`All Opportunities`}
          selected={selectedCountsFilter === 'ALL_OPPORTUNITIES'}
          onClick={() => onChangeFilter('ALL_OPPORTUNITIES')}
        />
      )}
      {isEnabledCrown && (
        <FilterButton
          count={
            <RecommendationCount
              count={filterCounts.crown}
              selected={selectedCountsFilter === 'crown'}
            />
          }
          dataTestId="crown-filter"
          loading={loading}
          label={getRecommendationName('crown')}
          selected={selectedCountsFilter === 'crown'}
          onClick={() => onChangeFilter('crown')}
        />
      )}
      {isEnabledEnamelRegen && (
        <FilterButton
          count={
            <RecommendationCount
              count={filterCounts.enamelRegen}
              selected={selectedCountsFilter === 'enamelRegen'}
            />
          }
          dataTestId="enamel-regen-filter"
          loading={loading}
          label={getRecommendationName('enamelRegen')}
          selected={selectedCountsFilter === 'enamelRegen'}
          onClick={() => onChangeFilter('enamelRegen')}
        />
      )}
      {isEnabledPerio && (
        <FilterButton
          count={
            <RecommendationCount
              count={filterCounts.perio}
              selected={selectedCountsFilter === 'perio'}
            />
          }
          dataTestId="perio-filter"
          loading={loading}
          label={getRecommendationName('perio')}
          selected={selectedCountsFilter === 'perio'}
          onClick={() => onChangeFilter('perio')}
        />
      )}
      {isExpanded && (
        <>
          {isEnabledRootCanalFilter && (
            <FilterButton
              count={
                <RecommendationCount
                  count={filterCounts.rootCanal}
                  selected={selectedCountsFilter === 'rootCanal'}
                />
              }
              dataTestId="root-canal-filter"
              loading={loading}
              label={getRecommendationName('rootCanal')}
              selected={selectedCountsFilter === 'rootCanal'}
              onClick={() => onChangeFilter('rootCanal')}
            />
          )}
          {isEnabledFilling && (
            <FilterButton
              count={
                <RecommendationCount
                  count={filterCounts.filling}
                  selected={selectedCountsFilter === 'filling'}
                />
              }
              dataTestId="filling-filter"
              loading={loading}
              label={getRecommendationName('filling')}
              selected={selectedCountsFilter === 'filling'}
              onClick={() => onChangeFilter('filling')}
            />
          )}
          {isEnabledExtraction && (
            <FilterButton
              count={
                <RecommendationCount
                  count={filterCounts.extraction}
                  selected={selectedCountsFilter === 'extraction'}
                />
              }
              dataTestId="extraction-filter"
              loading={loading}
              label={getRecommendationName('extraction')}
              selected={selectedCountsFilter === 'extraction'}
              onClick={() => onChangeFilter('extraction')}
            />
          )}
          {isEnabledImplant && (
            <FilterButton
              count={
                <RecommendationCount
                  count={filterCounts.implant}
                  selected={selectedCountsFilter === 'implant'}
                />
              }
              dataTestId="implant-filter"
              loading={loading}
              label={getRecommendationName('implant')}
              selected={selectedCountsFilter === 'implant'}
              onClick={() => onChangeFilter('implant')}
            />
          )}
          {isEnabledAligner && (
            <FilterButton
              count={
                <RecommendationCount
                  count={filterCounts.aligner}
                  selected={selectedCountsFilter === 'aligner'}
                />
              }
              dataTestId="ortho-filter"
              loading={loading}
              label={getRecommendationName('aligner')}
              selected={selectedCountsFilter === 'aligner'}
              onClick={() => onChangeFilter('aligner')}
            />
          )}
        </>
      )}
      {canExpand && !loading && (
        <ViewAllButton
          dataTestId="view-all-recommendations"
          expanded={isExpanded}
          onChange={handleChangeExpanded}
        />
      )}
    </>
  );
};
